import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import api, { formatApiError } from '../lib/api';
import { useAuth } from '../contexts/AuthContext';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { toast } from 'sonner';
import { Shield, Check, X } from 'lucide-react';

const COMPLEXITY = [
  { test: (p) => p.length >= 8, label: 'At least 8 characters' },
  { test: (p) => /[A-Z]/.test(p), label: 'One uppercase letter' },
  { test: (p) => /[0-9]/.test(p), label: 'One number' },
  // eslint-disable-next-line no-useless-escape
  { test: (p) => /[!@#\$%\^&\*\(\)_\+\-=\[\]\{\};:'",.<>\/?\\|`~]/.test(p), label: 'One special character (!@#$ etc.)' },
];

export default function ChangePassword() {
  const { refresh, user } = useAuth();
  const nav = useNavigate();
  const [cur, setCur] = useState('');
  const [nw, setNw] = useState('');
  const [conf, setConf] = useState('');
  const [busy, setBusy] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    const failed = COMPLEXITY.find(c => !c.test(nw));
    if (failed) return toast.error(failed.label + ' is required');
    if (nw !== conf) return toast.error('Passwords do not match');
    if (nw === cur) return toast.error('New password must be different from your current one');
    setBusy(true);
    try {
      await api.post('/auth/change-password', { current_password: cur, new_password: nw });
      toast.success('Password updated. Next password change in 15 days.');
      await refresh();
      nav('/dashboard');
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setBusy(false); }
  };

  const expired = user?.password_expiry?.expired;

  return (
    <div className="min-h-screen bg-[#F8FAFC] flex items-center justify-center px-4 py-12" data-testid="change-password-page">
      <div className="w-full max-w-md bg-white border border-slate-200 rounded-2xl p-8 shadow-sm">
        <div className="w-12 h-12 rounded-xl bg-[#3D4A8E] text-white flex items-center justify-center mb-5"><Shield className="w-6 h-6" /></div>
        <h1 className="font-heading text-2xl font-bold text-slate-900">{expired ? 'Time to refresh your password' : 'Set your new password'}</h1>
        <p className="text-sm text-slate-500 mt-2">
          {expired
            ? `Your password is older than 15 days. Please set a fresh one to continue.`
            : `Welcome${user?.name ? `, ${user.name.split(' ')[0]}` : ''}! For security, please change the temporary password emailed to you. After this, all logins will require a one-time code sent to your work email.`}
        </p>
        <form onSubmit={submit} className="space-y-4 mt-6" data-testid="change-pw-form">
          <div>
            <Label className="text-xs font-bold uppercase tracking-wider text-slate-600">Current password</Label>
            <Input data-testid="cur-password" type="password" required value={cur} onChange={(e) => setCur(e.target.value)} className="rounded-xl mt-2 h-12" />
          </div>
          <div>
            <Label className="text-xs font-bold uppercase tracking-wider text-slate-600">New password</Label>
            <Input data-testid="new-password" type="password" required value={nw} onChange={(e) => setNw(e.target.value)} className="rounded-xl mt-2 h-12" />
            <div className="mt-2 space-y-1" data-testid="cp-complexity-checklist">
              {COMPLEXITY.map((c, i) => {
                const ok = c.test(nw);
                return (
                  <div key={i} className={`text-xs flex items-center gap-1.5 ${ok ? 'text-emerald-700' : 'text-slate-400'}`}>
                    {ok ? <Check className="w-3 h-3" /> : <X className="w-3 h-3" />} {c.label}
                  </div>
                );
              })}
            </div>
          </div>
          <div>
            <Label className="text-xs font-bold uppercase tracking-wider text-slate-600">Confirm new password</Label>
            <Input data-testid="confirm-password" type="password" required value={conf} onChange={(e) => setConf(e.target.value)} className="rounded-xl mt-2 h-12" />
          </div>
          <Button type="submit" disabled={busy} data-testid="change-pw-submit" className="w-full h-12 rounded-full bg-[#3D4A8E] hover:bg-[#5C6BC0]">
            {busy ? 'Updating…' : (expired ? 'Update password' : 'Update password & enable MFA')}
          </Button>
        </form>
      </div>
    </div>
  );
}
